import i18n from 'i18next';

/**
 * On-demand translation catalogues.
 *
 * The 55 catalogues in this directory total ~4.6 MB. They used to be 55 static
 * `import` statements in ./index.ts, which put every one of them in main.js — so an
 * English visitor downloaded all 55 languages in order to read one. They are loaded
 * through dynamic `import()` here instead, which makes webpack emit one chunk per
 * locale and fetch only the active one.
 *
 * en-US is deliberately absent: it is `fallbackLng` and the canonical prerendered
 * language, so ./index.ts keeps importing it statically. That costs 60 KB and buys
 * two things — `t()` never returns a raw key before a chunk resolves, and the
 * English pages (which are most of them) need no extra round trip at all.
 *
 * This module imports the bare `i18next` singleton rather than ./index.ts on
 * purpose. ./index.ts calls `i18n.use(initReactI18next)`, and importing it from a
 * component would drag react-i18next's real module into suites that mock it —
 * `initReactI18next` comes back undefined and `use()` throws. Importing the
 * singleton directly gives access to the same instance with no side effects.
 */

/** A locale's catalogue, as webpack hands back a JSON module. */
type LocaleModule = { default: Record<string, unknown> };

/**
 * Dynamic import per locale, keyed by i18next language tag.
 *
 * Written out one entry per line rather than built from a template string:
 * webpack has to see a static specifier in each `import()` to know which files to
 * turn into chunks, and `import('./' + locale + '.json')` would instead bundle the
 * whole directory into one chunk — the problem this replaces.
 */
export const LOCALE_LOADERS: Record<string, () => Promise<LocaleModule>> = {
    'ar-SA': () => import(/* webpackChunkName: "locale-ar-SA" */ './ar-SA.json'),
    'bg-BG': () => import(/* webpackChunkName: "locale-bg-BG" */ './bg-BG.json'),
    'ca-ES': () => import(/* webpackChunkName: "locale-ca-ES" */ './ca-ES.json'),
    'cs-CZ': () => import(/* webpackChunkName: "locale-cs-CZ" */ './cs-CZ.json'),
    'cy-GB': () => import(/* webpackChunkName: "locale-cy-GB" */ './cy-GB.json'),
    'da-DK': () => import(/* webpackChunkName: "locale-da-DK" */ './da-DK.json'),
    'de-AT': () => import(/* webpackChunkName: "locale-de-AT" */ './de-AT.json'),
    'de-DE': () => import(/* webpackChunkName: "locale-de-DE" */ './de-DE.json'),
    'en-AU': () => import(/* webpackChunkName: "locale-en-AU" */ './en-AU.json'),
    'en-CA': () => import(/* webpackChunkName: "locale-en-CA" */ './en-CA.json'),
    'en-GB': () => import(/* webpackChunkName: "locale-en-GB" */ './en-GB.json'),
    'es-AR': () => import(/* webpackChunkName: "locale-es-AR" */ './es-AR.json'),
    'es-ES': () => import(/* webpackChunkName: "locale-es-ES" */ './es-ES.json'),
    'es-MX': () => import(/* webpackChunkName: "locale-es-MX" */ './es-MX.json'),
    'et-EE': () => import(/* webpackChunkName: "locale-et-EE" */ './et-EE.json'),
    'eu-ES': () => import(/* webpackChunkName: "locale-eu-ES" */ './eu-ES.json'),
    'fi-FI': () => import(/* webpackChunkName: "locale-fi-FI" */ './fi-FI.json'),
    'fr-CA': () => import(/* webpackChunkName: "locale-fr-CA" */ './fr-CA.json'),
    'fr-FR': () => import(/* webpackChunkName: "locale-fr-FR" */ './fr-FR.json'),
    'ga-IE': () => import(/* webpackChunkName: "locale-ga-IE" */ './ga-IE.json'),
    'gl-ES': () => import(/* webpackChunkName: "locale-gl-ES" */ './gl-ES.json'),
    'he-IL': () => import(/* webpackChunkName: "locale-he-IL" */ './he-IL.json'),
    'hi-IN': () => import(/* webpackChunkName: "locale-hi-IN" */ './hi-IN.json'),
    'hr-HR': () => import(/* webpackChunkName: "locale-hr-HR" */ './hr-HR.json'),
    'hu-HU': () => import(/* webpackChunkName: "locale-hu-HU" */ './hu-HU.json'),
    'id-ID': () => import(/* webpackChunkName: "locale-id-ID" */ './id-ID.json'),
    'is-IS': () => import(/* webpackChunkName: "locale-is-IS" */ './is-IS.json'),
    'it-IT': () => import(/* webpackChunkName: "locale-it-IT" */ './it-IT.json'),
    'ja-JP': () => import(/* webpackChunkName: "locale-ja-JP" */ './ja-JP.json'),
    'ko-KR': () => import(/* webpackChunkName: "locale-ko-KR" */ './ko-KR.json'),
    'lt-LT': () => import(/* webpackChunkName: "locale-lt-LT" */ './lt-LT.json'),
    'lv-LV': () => import(/* webpackChunkName: "locale-lv-LV" */ './lv-LV.json'),
    'mk-MK': () => import(/* webpackChunkName: "locale-mk-MK" */ './mk-MK.json'),
    'ms-MY': () => import(/* webpackChunkName: "locale-ms-MY" */ './ms-MY.json'),
    'mt-MT': () => import(/* webpackChunkName: "locale-mt-MT" */ './mt-MT.json'),
    'nl-NL': () => import(/* webpackChunkName: "locale-nl-NL" */ './nl-NL.json'),
    'no-NO': () => import(/* webpackChunkName: "locale-no-NO" */ './no-NO.json'),
    'pl-PL': () => import(/* webpackChunkName: "locale-pl-PL" */ './pl-PL.json'),
    'pt-BR': () => import(/* webpackChunkName: "locale-pt-BR" */ './pt-BR.json'),
    'pt-PT': () => import(/* webpackChunkName: "locale-pt-PT" */ './pt-PT.json'),
    'ro-RO': () => import(/* webpackChunkName: "locale-ro-RO" */ './ro-RO.json'),
    'ru-RU': () => import(/* webpackChunkName: "locale-ru-RU" */ './ru-RU.json'),
    'sk-SK': () => import(/* webpackChunkName: "locale-sk-SK" */ './sk-SK.json'),
    'sl-SI': () => import(/* webpackChunkName: "locale-sl-SI" */ './sl-SI.json'),
    'sq-AL': () => import(/* webpackChunkName: "locale-sq-AL" */ './sq-AL.json'),
    'sr-RS': () => import(/* webpackChunkName: "locale-sr-RS" */ './sr-RS.json'),
    'sv-SE': () => import(/* webpackChunkName: "locale-sv-SE" */ './sv-SE.json'),
    'th-TH': () => import(/* webpackChunkName: "locale-th-TH" */ './th-TH.json'),
    'tl-PH': () => import(/* webpackChunkName: "locale-tl-PH" */ './tl-PH.json'),
    'tr-TR': () => import(/* webpackChunkName: "locale-tr-TR" */ './tr-TR.json'),
    'uk-UA': () => import(/* webpackChunkName: "locale-uk-UA" */ './uk-UA.json'),
    'vi-VN': () => import(/* webpackChunkName: "locale-vi-VN" */ './vi-VN.json'),
    'zh-CN': () => import(/* webpackChunkName: "locale-zh-CN" */ './zh-CN.json'),
    'zh-TW': () => import(/* webpackChunkName: "locale-zh-TW" */ './zh-TW.json'),
    // Alias: i18next may resolve Filipino as fil-PH; same catalogue as tl-PH.
    'fil-PH': () => import(/* webpackChunkName: "locale-tl-PH" */ './tl-PH.json'),};

/** In-flight and settled loads, so concurrent callers share one request. */
const pending = new Map<string, Promise<void>>();

/**
 * Ensures `locale`'s catalogue is in the resource store.
 *
 * Resolves immediately for en-US (statically bundled), for a locale already
 * loaded, and for anything unrecognised — so callers can await it unconditionally.
 *
 * A no-op before init: nothing can be added to a store that does not exist yet.
 * The app never hits that path (./index.ts initialises at import time, above every
 * component), but test suites that render a component without importing ./index.ts
 * do, and they should not throw.
 */
export function loadLocale(locale: string): Promise<void> {
    if (!i18n.isInitialized) return Promise.resolve();
    if (i18n.hasResourceBundle(locale, 'translation')) return Promise.resolve();

    const loader = LOCALE_LOADERS[locale];
    if (!loader) return Promise.resolve();

    let inFlight = pending.get(locale);
    if (!inFlight) {
        inFlight = loader().then(module => {
            i18n.addResourceBundle(locale, 'translation', module.default, true, true);
        });
        pending.set(locale, inFlight);
    }
    return inFlight;
}

/**
 * Switches language, but only once its catalogue has arrived.
 *
 * The order matters. Calling `i18n.changeLanguage()` first would re-render every
 * subscriber against an empty bundle, so the page would flip to English and then to
 * the real language a moment later — a flash of the wrong content that the static
 * imports used to hide. Loading first means the single re-render lands already
 * translated.
 *
 * A no-op before init, for the same reason as `loadLocale`, and this guard has to be
 * here as well rather than relying on that one: `changeLanguage()` reaches into the
 * resource store directly and throws on an instance that has never been initialised.
 */
export async function changeLocale(locale: string): Promise<void> {
    if (!i18n.isInitialized) return;
    await loadLocale(locale);
    if (i18n.language !== locale) {
        await i18n.changeLanguage(locale);
    }
}
